import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import InventoryIntelligenceReport from "../Dealer/InventoryIntelligenceReport";

const API = process.env.REACT_APP_API_BASE;
const get = (value, key) => value?.[key] ?? value?.[key[0].toUpperCase() + key.slice(1)];

export default function StaffInventoryIntelligence() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("staffToken") || "", []);
  const [query, setQuery] = useState("");
  const [dealers, setDealers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [sort, setSort] = useState("difference_desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const selectedDealer = dealers.find((dealer) => String(get(dealer, "id")) === selectedId);

  const searchDealers = async (event) => {
    event.preventDefault();
    setSearching(true); setSearchError("");
    try {
      const params = new URLSearchParams({ page: "1", pageSize: "20", invited: "all", q: query.trim() });
      const response = await fetch(`${API}/staff/dealers?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      setDealers(payload.items ?? payload.Items ?? []);
    } catch {
      setDealers([]); setSearchError("Could not search dealers. Please try again.");
    } finally { setSearching(false); }
  };

  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    setLoading(true); setReportError(""); setData(null);
    fetch(`${API}/staff/dealers/${selectedId}/inventory-intelligence?sort=${encodeURIComponent(sort)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => { if (!response.ok) throw new Error(`HTTP ${response.status}`); return response.json(); })
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch(() => { if (!cancelled) setReportError("Could not load inventory intelligence. Please try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, sort, token]);

  const logout = () => {
    localStorage.removeItem("staffToken");
    navigate("/staff/login", { replace: true });
  };

  return <main className="min-h-screen bg-gray-50 px-4 py-8"><div className="mx-auto max-w-7xl">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h1 className="text-2xl font-semibold text-gray-900">Staff Inventory Intelligence</h1>
      <nav aria-label="Staff navigation" className="flex flex-wrap gap-2">
        <NavLink to="/staff" className="h-10 px-4 py-2 text-sm rounded-lg border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100">Staff Home</NavLink>
        <button type="button" onClick={logout} className="h-10 px-4 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200">Logout</button>
      </nav>
    </div>
    <p className="mt-1 text-sm text-gray-600">Select a dealer to view its Inventory Intelligence report.</p>
    <form onSubmit={searchDealers} className="mt-5 flex flex-col gap-2 sm:flex-row">
      <label className="sr-only" htmlFor="dealer-search">Search dealers</label>
      <input id="dealer-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Dealer name, DealerId, city, or state" className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2" />
      <button disabled={searching} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">{searching ? "Searching…" : "Search dealers"}</button>
    </form>
    {searchError && <div role="alert" className="mt-3 text-sm text-red-700">{searchError}</div>}
    {dealers.length > 0 && <div className="mt-4">
      <label htmlFor="dealer-select" className="mr-2 text-sm font-medium text-gray-700">Dealer:</label>
      <select id="dealer-select" aria-label="Select dealer" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2">
        <option value="">Select a dealer</option>
        {dealers.map((dealer) => <option key={get(dealer, "id")} value={get(dealer, "id")}>{get(dealer, "name")} — #{get(dealer, "id")} {get(dealer, "city") ? `— ${get(dealer, "city")}, ${get(dealer, "state") ?? ""}` : ""}</option>)}
      </select>
    </div>}
    {!selectedId ? <div className="mt-8 rounded-xl border bg-white p-8 text-center text-gray-600">Select a dealer to view Inventory Intelligence.</div> :
      <section className="mt-6">
        <div className="mb-3"><h2 className="text-xl font-semibold">{get(selectedDealer, "name")} <span className="text-sm font-normal text-gray-500">DealerId {selectedId}</span></h2>
          <p className="text-sm text-gray-500">{get(selectedDealer, "city")}{get(selectedDealer, "state") ? `, ${get(selectedDealer, "state")}` : ""} · DailyScrape: {get(selectedDealer, "dailyScrape") ? "Yes" : "No"}</p>
        </div>
        <InventoryIntelligenceReport data={data} loading={loading} error={reportError} sort={sort} onSortChange={setSort} />
      </section>}
  </div></main>;
}
